import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { User, Building2, Bell, Key, Clock } from 'lucide-react';
import { API_URL } from "../globals";
import { supabase } from "../supabaseClient";
import axios from "axios";
import NavBar from "../components/NavBar";
import ProfileTab from "../components/ProfileTab";
import OrganizationTab from "../components/OrganizationTab";
import NotificationsTab from "../components/NotificationsTab";
import APIKeysTab from "../components/APIKeysTab";
import CronJobsTab from "../components/CronJobsTab";
import { useNavigate } from "react-router-dom";

const TabItem = ({ icon: Icon, label, isSelected }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      width: '100%',
      pl: 2,
    }}
  >
    <Icon size={18} color={isSelected ? '#fff' : '#888'} />
    <Typography
      sx={{
        fontSize: '0.875rem',
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [broadcastPRSummaries, setBroadcastPRSummaries] = useState(false);
  const [isSavingBroadcastSetting, setIsSavingBroadcastSetting] = useState(false);
  
  // Team invitation states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('USER');
  const [isInviting, setIsInviting] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteError, setInviteError] = useState('');

  // Team members states
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState(null);

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  async function getUser() {
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/user`, {
        headers: headers,
      });

      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    // Set the initial state based on user's team attribute when user data is loaded
    if (user?.team?.broadcast_pr_summaries_on_slack !== undefined) {
      setBroadcastPRSummaries(user.team.broadcast_pr_summaries_on_slack);
    }
  }, [user]);

  useEffect(() => {
    // Fetch invitations when organization tab is active and user is loaded
    if (activeTab === 1 && user?.team?.id) {
      fetchTeamInvitations();
      fetchTeamMembers();
    }
  }, [activeTab, user]);

  const fetchTeamInvitations = async () => {
    if (!user?.team?.id) return;
    
    setIsLoadingInvitations(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/team-invitations?team_id=${user.team.id}`, {
        headers: headers,
      });

      if (response.data) {
        setInvitations(response.data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching team invitations:', error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  const fetchTeamMembers = async () => {
    if (!user?.team?.id) return;
    
    setIsLoadingMembers(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.get(`${API_URL}/get-team-members?team_id=${user.team.id}`, {
        headers: headers,
      });

      if (response.data) {
        setTeamMembers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleInviteTeamMember = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setIsInviting(true);
    setInviteError('');
    setInviteMessage('');

    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.post(
        `${API_URL}/invite-team-member`,
        {
          email: inviteEmail.trim(),
          team_id: user?.team?.id,
          role: inviteRole,
        },
        { headers }
      );

      if (response.status === 200) {
        setInviteMessage('Invitation sent successfully!');
        setInviteEmail('');
        setInviteRole('USER');
        // Refresh invitations list
        fetchTeamInvitations();
      }
    } catch (error) {
      console.error('Error inviting team member:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send invitation';
      setInviteError(errorMessage);
    } finally {
      setIsInviting(false);
    }
  };

  const getBroadcastPRSummariesSetting = async () => {
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/get-broadcast-pr-summaries`, {
        headers: headers,
      });

      if (response.data) {
        setBroadcastPRSummaries(response.data.enabled);
      }
    } catch (error) {
      console.error('Error fetching broadcast PR summaries setting:', error);
    }
  };

  const handleManageSubscription = async () => {
    setIsLoadingSubscription(true);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };
      
      const response = await axios.get(`${API_URL}/stripe/billing-portal`, {
        headers: headers,
      });

      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      alert('Error accessing billing portal');
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/signin');
    } catch (error) {
      console.error('Error logging out:', error);
      alert('Error logging out');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBroadcastPRSummariesChange = async (event) => {
    const newValue = event.target.checked;
    
    // Update UI immediately
    setBroadcastPRSummaries(newValue);
    if (user?.team) {
      setUser({
        ...user,
        team: {
          ...user.team,
          broadcast_pr_summaries_on_slack: newValue
        }
      });
    }
    
    // Send request in background
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      await axios.post(
        `${API_URL}/toggle_pr_summaries_broadcast`,
        { enabled: newValue },
        { headers }
      );
    } catch (error) {
      console.error('Error updating broadcast PR summaries setting:', error);
      // Revert the UI if the request failed
      setBroadcastPRSummaries(!newValue);
      if (user?.team) {
        setUser({
          ...user,
          team: {
            ...user.team,
            broadcast_pr_summaries_on_slack: !newValue
          }
        });
      }
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    const confirmRemove = window.confirm(
      `Are you sure you want to remove ${memberName} from the team? This action cannot be undone.`
    );
    
    if (!confirmRemove) return;

    setRemovingMemberId(memberId);
    try {
      const authToken = (await supabase.auth.getSession()).data.session.access_token;
      const headers = {
        Authorization: `Bearer ${authToken}`,
      };

      const response = await axios.post(
        `${API_URL}/remove-member-from-team`,
        {
          member_id: memberId,
          team_id: user?.team?.id,
        },
        { headers }
      );

      if (response.status === 200) {
        // Refresh team members list
        fetchTeamMembers();
        // Show success message (you could add a state for this if needed)
        console.log('Member removed successfully');
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove team member';
      alert(errorMessage);
    } finally {
      setRemovingMemberId(null);
    }
  };

  if (isLoading) {
    return (
      <>
        <NavBar user={user} />
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#121212'
        }}>
          <CircularProgress size={32} sx={{ color: '#fff' }} />
        </Box>
      </>
    );
  }

  return (
    <>
      <NavBar user={user} />
      <Box sx={{ 
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#121212',
      }}>
        {/* Settings Sidebar */}
        <Box sx={{
          width: '240px',
          borderRight: '1px solid #2a2a2a',
          pt: 4,
          px: 2,
          position: 'sticky',
          top: '64px', // Navbar height
          height: 'calc(100vh - 64px)',
          marginLeft: '240px', // Global sidebar width
        }}>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#666',
              fontWeight: 600,
              mb: 2,
              pl: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Settings
          </Typography>
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 'unset',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                minHeight: '40px',
                padding: '8px 0',
                borderRadius: '6px',
                mb: 0.5,
                '&.Mui-selected': {
                  color: '#fff',
                  backgroundColor: '#ffffff0a',
                },
              },
            }}
          >
            <Tab
              label={<TabItem icon={User} label="Profile" isSelected={activeTab === 0} />}
              sx={{
                color: '#888',
                textTransform: 'none',
                alignItems: 'flex-start',
                width: '100%',
              }}
            />
            <Tab
              label={<TabItem icon={Building2} label="Organization" isSelected={activeTab === 1} />}
              sx={{
                color: '#888',
                textTransform: 'none',
                alignItems: 'flex-start',
                width: '100%',
              }}
            />
            <Tab
              label={<TabItem icon={Bell} label="Notifications" isSelected={activeTab === 2} />}
              sx={{
                color: '#888',
                textTransform: 'none',
                alignItems: 'flex-start',
                width: '100%',
              }}
            />
            <Tab
              label={<TabItem icon={Key} label="API Keys" isSelected={activeTab === 3} />}
              sx={{
                color: '#888',
                textTransform: 'none',
                alignItems: 'flex-start',
                width: '100%',
              }}
            />
            <Tab
              label={<TabItem icon={Clock} label="Cron Jobs" isSelected={activeTab === 4} />}
              sx={{
                color: '#888',
                textTransform: 'none',
                alignItems: 'flex-start',
                width: '100%',
              }}
            />
          </Tabs>
        </Box>

        {/* Main Content */}
        <Box sx={{
          flexGrow: 1,
          px: 6,
          py: 4,
          minHeight: 'calc(100vh - 64px)',
        }}>
          <Box sx={{
            maxWidth: '1000px',
            margin: '0 auto',
          }}>
            <Box>
              {activeTab === 0 ? (
                <ProfileTab 
                  user={user} 
                  handleLogout={handleLogout} 
                />
              ) : activeTab === 1 ? (
                <OrganizationTab 
                  user={user}
                  handleManageSubscription={handleManageSubscription}
                  isLoadingSubscription={isLoadingSubscription}
                  broadcastPRSummaries={broadcastPRSummaries}
                  handleBroadcastPRSummariesChange={handleBroadcastPRSummariesChange}
                />
              ) : activeTab === 2 ? (
                <NotificationsTab />
              ) : activeTab === 3 ? (
                <APIKeysTab user={user} />
              ) : (
                <CronJobsTab user={user} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Profile; 